import { isLocalhostHttpOrigin, parseRegionTable, RegionTable } from '@deighton/shared-ui/core';

/** Validated runtime config from `window.__APP_CONFIG__` after {@link parseRuntimeConfig}. */
export interface ShellRuntimeConfig {
  /** Region allowlist keyed by region id; sourced from `public/config.js`. */
  regions: RegionTable;
}

/** `window` with the runtime config object assigned by `public/config.js`. */
export interface WindowWithAppConfig extends Window {
  /** Raw config payload before validation; shape is enforced by {@link parseRuntimeConfig}. */
  __APP_CONFIG__?: unknown;
}

/** Region key that may use `http://localhost:*` in development (see also `local2`, …). */
export const LOCAL_REGION_KEY: string = 'local';

/** Build flags {@link parseRuntimeConfig} depends on. */
export interface ShellRuntimeConfigParseOptions {
  /** When true, {@link LOCAL_REGION_KEY} and `local`+digits keys may use `http://localhost:*`. */
  readonly development: boolean;
}

/** Unvalidated `config.js` object shape before {@link parseRegionTable}. */
interface RawShellAppConfig {
  /** Raw `regions` map from `config.js`; may be missing or malformed. */
  regions?: unknown;
}

/**
 * True when `key` is {@link LOCAL_REGION_KEY} or `local` followed by digits only (e.g. `local2`).
 * @param key - Region table key from `window.__APP_CONFIG__.regions`.
 * @returns Whether the key is treated as a localhost development region.
 */
function isLocalDevRegionKey(key: string): boolean {
  return key === LOCAL_REGION_KEY || /^local[0-9]+$/.test(key);
}

/**
 * Validates a raw `window.__APP_CONFIG__` value and returns a frozen copy.
 * Called before Angular boots so the shell never starts without a valid region table.
 * @param raw - The value of `window.__APP_CONFIG__`.
 * @param options - Build flags.
 * @returns The frozen runtime configuration.
 * @throws {Error} Listing every problem found, so one deploy can fix them all.
 */
export function parseRuntimeConfig(
  raw: unknown,
  options: ShellRuntimeConfigParseOptions = { development: false }
): ShellRuntimeConfig {
  if (raw === undefined || raw === null || typeof raw !== 'object') {
    throw new Error(
      '[shell-app] window.__APP_CONFIG__ is not defined.\n' +
        'Ensure config.js is served and loaded before the Angular bundle.'
    );
  }

  const rawConfig: RawShellAppConfig = raw as RawShellAppConfig;
  const regions: RegionTable = parseRegionTable(rawConfig.regions, 'shell-app', {
    allowApiBaseUrl: (key: string, apiBaseUrl: string): boolean =>
      options.development && isLocalDevRegionKey(key) && isLocalhostHttpOrigin(apiBaseUrl),
  });
  return Object.freeze({ regions });
}

/**
 * Reads and validates `window.__APP_CONFIG__`.
 * @param options - Build flags.
 * @returns The frozen runtime configuration.
 */
export function loadRuntimeConfig(
  options: ShellRuntimeConfigParseOptions = { development: false }
): ShellRuntimeConfig {
  return parseRuntimeConfig((window as WindowWithAppConfig).__APP_CONFIG__, options);
}
